import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical, Pencil, Ban, RotateCcw, Trash2 } from 'lucide-react';
import { formatDate } from '../../../../lib/employeeHelpers';
import {
  Icon3dPayroll,
  Icon3dMySection,
  Icon3dConfig,
  Icon3dAssets,
  Icon3dReports,
} from '../../sidebar/Sidebar3dIcons';

/** Act tabs shared by StatutoryRatesManager (legacy mounts) and StatutoryRatesHeadsPanel. */
export const STATUTORY_ACT_TABS = [
  { id: 'pf', label: 'PF', icon: Icon3dPayroll },
  { id: 'esi', label: 'ESI', icon: Icon3dMySection },
  { id: 'pt', label: 'PT', icon: Icon3dConfig },
  { id: 'gratuity', label: 'Gratuity', icon: Icon3dAssets },
  { id: 'tax', label: 'Tax Rates', icon: Icon3dReports },
];

const TAB_ICON_SIZE = 22;

/** Multi-colour Soft3d icons stay full-colour (no desaturating rail class); wrapper pins the box size. */
export function ActTabBar({ tabs, value, onChange }) {
  return (
    <div className="flex flex-wrap items-center gap-1 border-b border-[hsl(var(--ds-border))]">
      {tabs.map((t) => {
        const Icon = t.icon;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={`-mb-px inline-flex items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors ${
              value === t.id
                ? 'border-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary))]'
                : 'border-transparent text-[hsl(var(--ds-muted-fg))] hover:text-[hsl(var(--ds-fg))]'
            }`}
          >
            {Icon && (
              <span
                className="inline-flex shrink-0"
                style={{ width: TAB_ICON_SIZE, height: TAB_ICON_SIZE }}
              >
                <Icon size={TAB_ICON_SIZE} />
              </span>
            )}
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

export function TaxRegimeSidebar({ value, onChange }) {
  return (
    <nav
      aria-label="Tax regime"
      className="flex w-44 shrink-0 flex-col gap-1 rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]/40 p-2"
    >
      {[
        { id: 'old', label: 'Old Regime', icon: Icon3dReports },
        { id: 'new', label: 'New Regime', icon: Icon3dPayroll },
      ].map((r) => {
        const Icon = r.icon;
        return (
          <button
            key={r.id}
            type="button"
            onClick={() => onChange(r.id)}
            className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-left text-sm font-medium transition-colors ${
              value === r.id
                ? 'bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]'
                : 'text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]'
            }`}
          >
            <span className="inline-flex shrink-0" style={{ width: 20, height: 20 }}>
              <Icon size={20} />
            </span>
            {r.label}
          </button>
        );
      })}
    </nav>
  );
}

const pct = (v) => (v == null || v === '' ? '—' : `${v}%`);
const inr = (v) =>
  v == null || v === '' || !Number.isFinite(Number(v)) ? '—' : `₹${Number(v).toLocaleString('en-IN')}`;
const lastRate = (list) => (Array.isArray(list) && list.length ? pct(list[list.length - 1]?.rate) : '—');

const taxColumns = [
  { label: 'Cess', render: (r) => pct(r.cess) },
  { label: 'Std. deduction', render: (r) => inr(r.standardDeduction) },
  { label: 'Tax slabs', render: (r) => (Array.isArray(r.slabs) ? r.slabs.length : '—') },
  { label: 'Top slab rate', render: (r) => lastRate(r.slabs) },
  { label: 'Surcharge slabs', render: (r) => (Array.isArray(r.surcharge) ? r.surcharge.length : '—') },
];

/** Grid columns per history section (excluding Effective Date / Status / menu). */
const COLUMNS = {
  pf: [
    { label: 'Employee', render: (r) => pct(r.employeeShare) },
    { label: 'Employer', render: (r) => pct(r.employerShare) },
    { label: 'PF ceiling', render: (r) => inr(r.pfWageCeiling ?? r.ceiling) },
    { label: 'EPF / EPS', render: (r) => `${r.epf ?? '—'}% / ${r.eps ?? '—'}%` },
    { label: 'Admin', render: (r) => pct(r.adminCharges) },
    { label: 'EDLI', render: (r) => pct(r.edliRate) },
  ],
  esi: [
    { label: 'Employee', render: (r) => pct(r.employeeShare) },
    { label: 'Employer', render: (r) => pct(r.employerShare) },
    { label: 'Ceiling', render: (r) => inr(r.ceiling) },
    { label: 'Cutoff', render: (r) => inr(r.cutoff) },
  ],
  pt: [
    { label: 'Frequency', render: (r) => (r.frequency === 'HALF_YEARLY' ? 'Half-yearly' : r.frequency ? 'Monthly' : '—') },
    { label: 'Annual cap', render: (r) => inr(r.annualCap) },
    { label: 'Slabs', render: (r) => (Array.isArray(r.slabs) ? r.slabs.length : '—') },
    { label: 'Levied', render: (r) => (r.applicable === false ? 'No' : 'Yes') },
  ],
  gratuity: [
    { label: 'Rate', render: (r) => pct(r.rate) },
    { label: 'Min yrs (Full-time)', render: (r) => r.minServiceYearsFullTime ?? 5 },
    { label: 'Min yrs (Contract)', render: (r) => r.minServiceYearsContract ?? 5 },
  ],
  incomeTaxOld: taxColumns,
  incomeTaxNew: taxColumns,
};

const todayIso = () => new Date().toISOString().slice(0, 10);

function MenuItem({ icon: Icon, label, onClick, disabled, title, danger }) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      title={title}
      onClick={onClick}
      className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[hsl(var(--ds-muted))] disabled:cursor-not-allowed disabled:opacity-45 ${
        danger ? 'text-[hsl(var(--ds-danger))]' : 'text-[hsl(var(--ds-fg))]'
      }`}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

/**
 * Saved-rates grid + 3-dot portal menu for one statutory act.
 * `entries` = full history list for the section (any order); filtering is local.
 * Rules enforced here (UI) and again in StatutoryRatesManager handlers:
 *  - Delete: newest entry only, never the only entry
 *  - Deactivate/Delete: never the oldest (baseline) entry
 */
export default function StatutoryActHistoryList({
  section,
  entries = [],
  search = '',
  showInactive = false,
  canEdit = true,
  busy = false,
  onEdit,
  onToggleActive,
  onDelete,
}) {
  const [menu, setMenu] = useState(null); // { key, entry, top, left }
  const menuRef = useRef(null);
  const columns = COLUMNS[section] || [];

  const sorted = useMemo(
    () => [...entries].sort((a, b) => new Date(b.date) - new Date(a.date)),
    [entries]
  );
  const today = todayIso();
  const liveKey = useMemo(() => {
    const live = sorted.find((e) => e.active !== false && String(e.date).slice(0, 10) <= today);
    return live ? String(live.date).slice(0, 10) : null;
  }, [sorted, today]);

  const rows = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    return sorted
      .map((entry, idx) => {
        const d = String(entry.date).slice(0, 10);
        const inactive = entry.active === false;
        const status = inactive
          ? 'Inactive'
          : d > today
          ? 'Upcoming'
          : d === liveKey
          ? 'Current'
          : 'Historical';
        return { entry, idx, d, inactive, status };
      })
      .filter((row) => {
        if (!showInactive && row.inactive) return false;
        if (!q) return true;
        const cells = columns.map((c) => String(c.render(row.entry.rates || {})));
        return [row.d, formatDate(row.entry.date), row.entry.changes, row.status, ...cells]
          .join(' ')
          .toLowerCase()
          .includes(q);
      });
  }, [sorted, search, showInactive, columns, liveKey, today]);

  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => {
      if (e.type === 'keydown' && e.key !== 'Escape') return;
      if (e.type === 'mousedown' && menuRef.current?.contains(e.target)) return;
      setMenu(null);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', close);
    window.addEventListener('resize', close);
    window.addEventListener('scroll', close, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', close);
      window.removeEventListener('resize', close);
      window.removeEventListener('scroll', close, true);
    };
  }, [menu]);

  const openMenu = (e, row) => {
    e.stopPropagation();
    if (menu?.key === row.d) {
      setMenu(null);
      return;
    }
    const rect = e.currentTarget.getBoundingClientRect();
    const MENU_W = 192;
    const MENU_H = 130;
    const top = rect.bottom + MENU_H > window.innerHeight ? rect.top - MENU_H - 4 : rect.bottom + 4;
    setMenu({
      key: row.d,
      row,
      top: Math.max(8, top),
      left: Math.max(8, rect.right - MENU_W),
    });
  };

  const gridCols = `minmax(110px,1.1fr) repeat(${columns.length}, minmax(80px,1fr)) minmax(90px,0.9fr) 40px`;
  const total = sorted.length;
  const act = (fn, row) => {
    setMenu(null);
    fn?.(row.entry);
  };

  return (
    <div>
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
        Saved list
      </p>
      <div className="overflow-x-auto rounded-xl border border-[hsl(var(--ds-border))]">
        <div className="min-w-[640px]">
          <div
            className="grid items-center gap-2 border-b border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] px-3 py-2 text-xs font-semibold text-[hsl(var(--ds-muted-fg))]"
            style={{ gridTemplateColumns: gridCols }}
          >
            <span>Effective date</span>
            {columns.map((c) => (
              <span key={c.label}>{c.label}</span>
            ))}
            <span>Status</span>
            <span />
          </div>
          {rows.map((row) => {
            const isNewest = row.idx === 0;
            const isOldest = row.idx === total - 1;
            return (
              <div
                key={`${row.d}::${row.entry.id ?? row.idx}`}
                className={`grid items-center gap-2 border-b border-[hsl(var(--ds-border))] px-3 py-2 text-sm last:border-b-0 ${
                  row.inactive ? 'opacity-60' : ''
                }`}
                style={{ gridTemplateColumns: gridCols }}
              >
                <span className="font-medium text-[hsl(var(--ds-fg))]">{formatDate(row.entry.date)}</span>
                {columns.map((c) => (
                  <span key={c.label} className="truncate text-[hsl(var(--ds-fg))]">
                    {c.render(row.entry.rates || {})}
                  </span>
                ))}
                <span>
                  <span
                    className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      row.status === 'Current'
                        ? 'bg-green-100 text-green-800'
                        : row.status === 'Upcoming'
                        ? 'bg-blue-100 text-blue-800'
                        : row.status === 'Inactive'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]'
                    }`}
                  >
                    {row.status}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="Row actions"
                  aria-haspopup="menu"
                  disabled={!canEdit || busy}
                  onClick={(e) => openMenu(e, row)}
                  className="rounded-md p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] disabled:opacity-40"
                >
                  <MoreVertical size={16} />
                </button>
                {menu?.key === row.d &&
                  createPortal(
                    <div
                      ref={menuRef}
                      role="menu"
                      style={{ position: 'fixed', top: menu.top, left: menu.left }}
                      className="z-[10011] w-48 rounded-lg border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] py-1 shadow-lg"
                    >
                      <MenuItem icon={Pencil} label="Edit" onClick={() => act(onEdit, row)} />
                      {row.inactive ? (
                        <MenuItem
                          icon={RotateCcw}
                          label="Reactivate"
                          onClick={() => act(onToggleActive, row)}
                        />
                      ) : (
                        <MenuItem
                          icon={Ban}
                          label="Deactivate"
                          disabled={isOldest}
                          title={isOldest ? 'The baseline (oldest) entry cannot be deactivated' : undefined}
                          onClick={() => act(onToggleActive, row)}
                        />
                      )}
                      <MenuItem
                        icon={Trash2}
                        label="Delete"
                        danger
                        disabled={total <= 1 || isOldest || !isNewest}
                        title={
                          total <= 1
                            ? 'Cannot delete the only entry'
                            : isOldest
                            ? 'The baseline (oldest) entry cannot be deleted'
                            : !isNewest
                            ? 'Only the newest entry can be deleted (remove newest first)'
                            : undefined
                        }
                        onClick={() => act(onDelete, row)}
                      />
                    </div>,
                    document.body
                  )}
              </div>
            );
          })}
          {rows.length === 0 && (
            <p className="px-3 py-6 text-center text-sm text-[hsl(var(--ds-muted-fg))]">
              {total === 0 ? 'No saved rates yet.' : 'No matching entries.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
